import { useState } from 'react';
import { CSVLink } from 'react-csv';
import { Link, useNavigate } from 'react-router-dom';
import { DeleteOutlined } from '@ant-design/icons';
import {
  Access,
  AccessRule,
  AccessRuleConflictView,
  AccessRuleException,
  AccessRuleOwner,
  AccessRuleType,
  User,
} from '@prisma/client';
import { Breadcrumb, Button, Input, Layout, message } from 'antd';
import Table from 'antd/es/table';
import _ from 'lodash';

import { Role } from '@app/domain-interfaces';
import { formatIsoDate } from '@app/utils-agnostic';
import { Auth, ConfirmDelete, PageLayout, Tables } from '@app/web-components';

import { AuditFilters, CreatedByColumn } from '../../../common';
import { useFindAccessRuleExceptions, useRemoveAccessRuleException } from '../../rules/data/access-rule-exceptions';
import { AccessRuleTypeRenderer } from '../../rules/libs';

type ExceptionsLandingItem = AccessRuleException & {
  accessRule: AccessRule & {
    access: Access;
    conflict: AccessRuleConflictView;
    owners: AccessRuleOwner[];
  };
  user: User;
  createdByUser: User;
};

export const AccessRuleExceptionsLandingPage = () => {
  const { user } = Auth.useUserAuth();
  const [, messageContextHolder] = message.useMessage();
  const navigate = useNavigate();
  const [deleteTarget, setDeleteTarget] = useState<ExceptionsLandingItem | undefined>(undefined);

  const { data: exceptionAccessRuleList } = useFindAccessRuleExceptions<AccessRuleException>({
    where: {},
    include: {
      accessRule: {
        include: {
          access: { select: { name: true } },
          conflict: { select: { name: true } },
        },
      },
    },
  });

  const removeAccessRuleException = useRemoveAccessRuleException();

  // server side table state
  const {
    getCreateButtonProps,
    getCSVLinkProps,
    getParentLayoutContentProps,
    getReloadButtonProps,
    getResetFiltersButtonProps,
    getQuery,
    getSearchInputProps,
    getTableProps,
    getToolbarProps,
  } = Tables.useTableState<ExceptionsLandingItem>(
    'Access Rule Exception',
    [
      {
        title: 'Action',
        width: 120,
        render: (_, record) => (
          <Button
            icon={<DeleteOutlined />}
            size="small"
            disabled={
              !user?.roles[Role.SYSTEM_ADMIN] &&
              !(
                user?.roles[Role.RULE_ADMIN] &&
                _.accessRule.owners?.some((owner: { userId: number }) => owner.userId === user?.id)
              )
            }
            onClick={() => setDeleteTarget(record)}
          >
            Delete
          </Button>
        ),
      },
      {
        title: 'WWID',
        width: 120,
        dataIndex: ['user', 'enterpriseId'],
        sorter: true,
      },
      {
        title: 'Name',
        width: 180,
        dataIndex: ['user', 'name'],
        ellipsis: true,
        sorter: true,
      },
      {
        title: 'Rule Type',
        dataIndex: ['accessRule', 'conflict', 'ruleType'],
        ellipsis: true,
        sorter: true,
        width: 225,
        filters: [
          { text: 'Access Conflict', value: AccessRuleType.ACCESS_CONFLICT },
          { text: 'Department Conflict', value: AccessRuleType.DEPARTMENT_CONFLICT },
        ],
        filterMultiple: false,
        render: AccessRuleTypeRenderer,
      },
      {
        title: 'Access',
        dataIndex: ['accessRule', 'access', 'name'],
        ellipsis: true,
        sorter: true,
        filters: _.uniq(_.map(exceptionAccessRuleList?.data, 'accessRule.access.name'))
          .sort()
          .map((accessName) => ({
            text: accessName,
            value: accessName,
          })),
      },
      {
        title: 'Conflict',
        dataIndex: ['accessRule', 'conflict', 'name'],
        ellipsis: true,
        sorter: true,
        filters: _.uniq(_.map(exceptionAccessRuleList?.data, 'accessRule.conflict.name'))
          .sort()
          .map((accessName) => ({
            text: accessName,
            value: accessName,
          })),
      },
      {
        title: 'Justification',
        dataIndex: 'justification',
        ellipsis: true,
        sorter: true,
      },
      CreatedByColumn,
      {
        title: 'Created On',
        render: (date: string) => formatIsoDate(date),
        dataIndex: ['createdOn'],
        sorter: true,
        defaultSortOrder: 'descend',
      },
    ],
    {
      customFilters: { 'createdByUser.name': AuditFilters['createdByUser.name'] },
      globalSearchFields: ['user.enterpriseId', 'user.name'],
    },
  );

  const queryResult = useFindAccessRuleExceptions<ExceptionsLandingItem>(
    getQuery({
      include: {
        accessRule: {
          include: {
            access: { select: { name: true } },
            conflict: true,
            owners: { select: { userId: true } },
          },
        },
        user: { select: { name: true, enterpriseId: true } },
        createdByUser: { select: { name: true } },
      },
    }),
  );

  return (
    <>
      <ConfirmDelete.Modal
        itemId={deleteTarget?.id}
        onClose={() => setDeleteTarget(undefined)}
        onOk={removeAccessRuleException.mutate}
      />
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[{ title: <Link to="/">Home</Link> }, { title: 'Admin' }, { title: 'Access Rule Exceptions' }]}
      />
      <Layout.Content {...getParentLayoutContentProps()}>
        <PageLayout.Title title="Access Rule Exceptions" />

        <div
          {...getToolbarProps({
            items: [
              <Input {...getSearchInputProps()} placeholder="Search by user name/wwid ..." />,
              user?.roles[Role.RULE_ADMIN] ? (
                <Button {...getCreateButtonProps()} onClick={() => navigate(`./manage`)}>
                  Manage
                </Button>
              ) : undefined,
              <Button {...getReloadButtonProps()} onClick={() => queryResult.refetch()} />,
              <Button {...getResetFiltersButtonProps()} />,
              <CSVLink
                {...getCSVLinkProps(queryResult.data?.data, {
                  'accessRule.conflict.ruleType': AccessRuleTypeRenderer,
                })}
              />,
            ],
          })}
        />
        <Table {...getTableProps(queryResult)} />
        {messageContextHolder}
      </Layout.Content>
    </>
  );
};
